import { faGasPump } from '@fortawesome/pro-solid-svg-icons';
import { FixtureTypes } from '@shared/utils/constants';
import type { GetTcPnlResponse } from '@api/features/voyages/getTcPnl';
import { ValidBunkerModalTypes } from '@client/utils/hooks/useBunkerModal';

const getTcPnlItems = (
	pnl: GetTcPnlResponse,
	handleModalOpen: (type: ValidBunkerModalTypes) => void,
	showTcInRedeliveryBunkers?: boolean,
	expandedTotalDays?: boolean,
	isTcInVessel?: boolean,
	isEuEtsIceClassDiscounted?: boolean,
) => {
	const revenueItems = [
		{
			item: 'Gross Hire',
			type: 'revenue',
			...pnl.grossHire,
		},
		{
			item: 'Gross Ballast Bonus',
			type: 'revenue',
			...pnl.grossBallastBonus,
		},
		{
			item: 'Address Commissions',
			type: 'revenue',
			...pnl.addressCommission,
		},
		{
			item: 'Broker Commissions',
			type: 'revenue',
			...pnl.brokerCommission,
		},
		{
			item: 'Expenses Subject To Hire Days',
			type: 'revenue',
			...pnl.expensesSubjectToHireDays,
		},
		{
			item: 'Charterer\'s Expenses',
			type: 'revenue',
			...pnl.chartererExpenses,
		},
		{
			item: 'Net Revenue',
			type: 'revenue',
			bold: true,
			...pnl.totalRevenue,
		},
	];

	const expenseItems = [
		{
			item: 'Other Expenses',
			type: 'expense',
			...pnl.ownersExpenses,
		},
		{
			item: 'Off-Hire',
			type: 'expense',
			...pnl.offHire,
		},
		{
			item: 'Bunker Expenditure',
			type: 'expense',
			...pnl.bunkerExpenditure,
		},
		{
			item: isEuEtsIceClassDiscounted ? 'EU ETS Costs (-5%)' : 'EU ETS Costs',
			type: 'expense',
			...pnl.euEts,
		},
		{
			item: 'UK ETS',
			type: 'expense',
			...pnl.ukEts,
		},
		{
			item: 'FuelEU penalty',
			type: 'expense',
			...pnl.fuelEuPenalty,
		},
		{
			item: 'TC Out Delivery Bunker Gain / Loss',
			type: 'revenue',
			onClick: () => handleModalOpen(FixtureTypes.TC_OUT),
			icon: faGasPump,
			...pnl.tcOutDeliveryBunkerGainLoss,
		},
		...(showTcInRedeliveryBunkers ? [
			{
				item: 'TC In Redelivery Bunker Gain / Loss',
				type: 'revenue',
				onClick: () => handleModalOpen(FixtureTypes.TC_IN),
				icon: faGasPump,
				...pnl.tcInRedeliveryBunkerGainLoss,
			},
		] : []),
		{
			item: 'Total Voyage Expenses',
			type: 'expense',
			bold: true,
			...pnl.totalExpenses,
		},
	];

	const resultsItems = [
		{
			item: 'TC Earnings',
			bold: true,
			type: 'result',
			...pnl.voyageProfit,
		},
		{
			item: 'Total Days',
			number: true,
			bold: true,
			type: 'result',
			...pnl.voyageDays,
		},
		...(expandedTotalDays && pnl.tcOutDays != null ? [{
			item: '- TC Out',
			number: true,
			type: 'result',
			...pnl.tcOutDays,
		}] : []),
		...(expandedTotalDays && pnl.tcBallastingDays != null ? [{
			item: '- Ballasting',
			number: true,
			type: 'result',
			...pnl.tcBallastingDays,
		}] : []),
		{
			item: 'TC Earnings per day',
			highlight: true,
			type: 'result',
			...pnl.tce,
		},
		...(!isTcInVessel ? [
			{
				item: 'CAPEX',
				type: 'result',
				...pnl.capex,
			},
			{
				item: 'OPEX',
				type: 'result',
				...pnl.opex,
			},
		] : []),
		{
			item: 'Net hire cost',
			type: 'result',
			...pnl.tcInCosts,
		},
		{
			item: 'Net Result',
			bold: true,
			type: 'result',
			...pnl.netResult,
		},
		{
			item: 'Net Result / day',
			highlight: true,
			type: 'result',
			...pnl.netResultPerDay,
		},
	];

	return {
		revenueItems,
		expenseItems,
		resultsItems,
	};
};

export default getTcPnlItems;
